
import { useState } from 'react';

interface Module {
  id: number;
  level: string;
  title: string;
  description: string;
  topics: string[];
  subtopics: string[];
  content: string;
  image: string;
}

interface ModuleEditorProps {
  module: Module;
  onSave: (module: Module) => void;
  onCancel: () => void;
}

export default function ModuleEditor({ module, onSave, onCancel }: ModuleEditorProps) {
  const [editedModule, setEditedModule] = useState<Module>({ ...module });
  const [newTopic, setNewTopic] = useState('');
  const [newSubtopic, setNewSubtopic] = useState('');

  const handleAddTopic = () => {
    if (newTopic.trim()) {
      setEditedModule({
        ...editedModule,
        topics: [...editedModule.topics, newTopic.trim()]
      });
      setNewTopic('');
    }
  };

  const handleRemoveTopic = (index: number) => {
    setEditedModule({
      ...editedModule,
      topics: editedModule.topics.filter((_, i) => i !== index)
    });
  };

  const handleAddSubtopic = () => {
    if (newSubtopic.trim()) {
      setEditedModule({
        ...editedModule,
        subtopics: [...editedModule.subtopics, newSubtopic.trim()]
      });
      setNewSubtopic('');
    }
  };

  const handleRemoveSubtopic = (index: number) => {
    setEditedModule({
      ...editedModule,
      subtopics: editedModule.subtopics.filter((_, i) => i !== index)
    });
  };

  const handleSave = () => {
    onSave(editedModule);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[95vh] overflow-y-auto">
        <div className="p-6 border-b bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold flex items-center">
              <i className="ri-edit-line mr-3"></i>
              Editor de Módulo
            </h2>
            <button 
              onClick={onCancel}
              className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Información Básica */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Título del Módulo</label>
                <input
                  type="text"
                  value={editedModule.title}
                  onChange={(e) => setEditedModule({ ...editedModule, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Ingresa el título del módulo"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Descripción</label>
                <textarea
                  value={editedModule.description}
                  onChange={(e) => setEditedModule({ ...editedModule, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Breve descripción del módulo"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">URL de Imagen</label>
                <input
                  type="text"
                  value={editedModule.image}
                  onChange={(e) => setEditedModule({ ...editedModule, image: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="URL de la imagen del módulo"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Nivel</label>
                <select
                  value={editedModule.level}
                  onChange={(e) => setEditedModule({ ...editedModule, level: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                >
                  <option value="PRINCIPIANTE">PRINCIPIANTE</option>
                  <option value="NOVATO">NOVATO</option>
                  <option value="INTERMEDIO">INTERMEDIO</option>
                  <option value="AVANZADO">AVANZADO</option>
                  <option value="EXPERTO">EXPERTO</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Contenido del Módulo</label>
                <textarea
                  value={editedModule.content}
                  onChange={(e) => setEditedModule({ ...editedModule, content: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Describe detalladamente qué se enseñará en este módulo"
                />
              </div>
            </div>

            {/* Temas y Subtemas */}
            <div className="space-y-6">
              {/* Temas Principales */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Temas Principales</label>
                <div className="space-y-2 mb-3">
                  {editedModule.topics.map((topic, index) => (
                    <div key={index} className="flex items-center gap-2 bg-blue-50 p-3 rounded-lg">
                      <i className="ri-check-line text-blue-500"></i>
                      <span className="flex-1 text-sm">{topic}</span>
                      <button
                        onClick={() => handleRemoveTopic(index)}
                        className="w-6 h-6 flex items-center justify-center bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTopic}
                    onChange={(e) => setNewTopic(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Agregar nuevo tema"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTopic()}
                  />
                  <button
                    onClick={handleAddTopic}
                    className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>

              {/* Subtemas */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Subtemas</label>
                <div className="space-y-2 mb-3">
                  {editedModule.subtopics.map((subtopic, index) => (
                    <div key={index} className="flex items-center gap-2 bg-purple-50 p-3 rounded-lg">
                      <i className="ri-arrow-right-s-line text-purple-500"></i>
                      <span className="flex-1 text-sm">{subtopic}</span>
                      <button
                        onClick={() => handleRemoveSubtopic(index)}
                        className="w-6 h-6 flex items-center justify-center bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSubtopic}
                    onChange={(e) => setNewSubtopic(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    placeholder="Agregar nuevo subtema"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddSubtopic()}
                  />
                  <button
                    onClick={handleAddSubtopic}
                    className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>

              {/* Vista Previa de Imagen */}
              {editedModule.image && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Vista Previa</label>
                  <img 
                    src={editedModule.image} 
                    alt="Vista previa"
                    className="w-full h-48 object-cover rounded-lg border"
                    onError={(e) => {
                      e.currentTarget.src = 'https://via.placeholder.com/400x300?text=Imagen+no+encontrada';
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Botones de Acción */}
          <div className="flex justify-end gap-4 mt-8 pt-6 border-t">
            <button
              onClick={onCancel}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-save-line mr-2"></i>
              Guardar Cambios
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
